import React, { createContext, useContext, useReducer, useMemo, ReactNode } from 'react';
import { Project, FrameworkTemplate } from '../models/types';
import { storageService } from '../services/storageService';
import { templateService } from '../services/templateService';

// State interface
interface AppState {
  currentProject: Project | null;
  projects: Project[];
  templates: FrameworkTemplate[];
  loading: boolean;
  uiState: {
    showProjectList: boolean;
    showAdminPage: boolean;
    sidebarWidth: number;
    useCubaDashboard: boolean;
  };
}

// Action types
type AppAction =
  | { type: 'SET_CURRENT_PROJECT'; payload: Project | null }
  | { type: 'SET_PROJECTS'; payload: Project[] }
  | { type: 'ADD_PROJECT'; payload: Project }
  | { type: 'UPDATE_PROJECT'; payload: Project }
  | { type: 'DELETE_PROJECT'; payload: string }
  | { type: 'SET_TEMPLATES'; payload: FrameworkTemplate[] }
  | { type: 'ADD_TEMPLATE'; payload: FrameworkTemplate }
  | { type: 'UPDATE_TEMPLATE'; payload: FrameworkTemplate }
  | { type: 'DELETE_TEMPLATE'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'TOGGLE_PROJECT_LIST' }
  | { type: 'TOGGLE_ADMIN_PAGE' }
  | { type: 'TOGGLE_DASHBOARD_VIEW' }
  | { type: 'SET_SIDEBAR_WIDTH'; payload: number };

// Initial state
const initialState: AppState = {
  currentProject: null,
  projects: [],
  templates: [],
  loading: true,
  uiState: {
    showProjectList: localStorage.getItem('requirements-tracker-sidebar-visible') === 'true',
    showAdminPage: false,
    sidebarWidth: Number(localStorage.getItem('requirements-tracker-sidebar-width') || '320'),
    useCubaDashboard: localStorage.getItem('requirements-tracker-use-cuba') === 'true',
  },
};

// Reducer function
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_CURRENT_PROJECT':
      return { ...state, currentProject: action.payload };
    
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload };
    
    case 'ADD_PROJECT':
      return { ...state, projects: [...state.projects, action.payload] };
    
    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map(p => 
          p.id === action.payload.id ? action.payload : p
        ),
        currentProject: state.currentProject?.id === action.payload.id 
          ? action.payload 
          : state.currentProject,
      };
    
    case 'DELETE_PROJECT':
      return {
        ...state,
        projects: state.projects.filter(p => p.id !== action.payload),
        currentProject: state.currentProject?.id === action.payload
          ? null
          : state.currentProject,
      };
    
    case 'SET_TEMPLATES':
      return { ...state, templates: action.payload };
    
    case 'ADD_TEMPLATE':
      return { ...state, templates: [...state.templates, action.payload] };
    
    case 'UPDATE_TEMPLATE':
      return {
        ...state,
        templates: state.templates.map(t => 
          t.id === action.payload.id ? action.payload : t
        ),
      };
    
    case 'DELETE_TEMPLATE':
      return {
        ...state,
        templates: state.templates.filter(t => t.id !== action.payload),
      };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'TOGGLE_PROJECT_LIST':
      const showProjectList = !state.uiState.showProjectList;
      localStorage.setItem('requirements-tracker-sidebar-visible', String(showProjectList));
      return {
        ...state,
        uiState: {
          ...state.uiState,
          showProjectList,
          showAdminPage: showProjectList ? false : state.uiState.showAdminPage,
        },
      };
    
    case 'TOGGLE_ADMIN_PAGE':
      return {
        ...state,
        uiState: {
          ...state.uiState,
          showAdminPage: !state.uiState.showAdminPage,
          showProjectList: false,
        },
      };
    
    case 'SET_SIDEBAR_WIDTH':
      localStorage.setItem('requirements-tracker-sidebar-width', action.payload.toString());
      return {
        ...state,
        uiState: {
          ...state.uiState,
          sidebarWidth: action.payload,
        },
      };
    
    case 'TOGGLE_DASHBOARD_VIEW':
      const useCubaDashboard = !state.uiState.useCubaDashboard;
      localStorage.setItem('requirements-tracker-use-cuba', String(useCubaDashboard));
      return {
        ...state,
        uiState: {
          ...state.uiState,
          useCubaDashboard,
        },
      };

    default:
      return state;
  }
}

// Context type
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Memoized actions for common operations
  selectProject: (project: Project) => void;
  createProject: (name: string, description: string) => void;
  saveProject: (project: Project) => void;
  deleteProject: (id: string) => void;
  createTemplate: (name: string, description: string) => void;
  saveTemplate: (template: FrameworkTemplate) => void;
  cloneTemplate: (id: string, newName: string) => FrameworkTemplate | null;
  deleteTemplate: (id: string) => void;
  toggleProjectList: () => void;
  toggleAdminPage: () => void;
  toggleDashboardView: () => void;
  resizeSidebar: (width: number) => void;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize data
  React.useEffect(() => {
    // Initialize storage if empty
    storageService.initializeIfEmpty();
    templateService.initializeIfEmpty();
    
    // Load projects
    const allProjects = storageService.getAllProjects();
    dispatch({ type: 'SET_PROJECTS', payload: allProjects });
    
    // Load templates
    const allTemplates = templateService.getAllTemplates();
    dispatch({ type: 'SET_TEMPLATES', payload: allTemplates });
    
    // Get current project
    const current = storageService.getCurrentProject();
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: current });
    
    // Set loading to false
    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  // Memoized action handlers
  const selectProject = useMemo(() => (project: Project) => {
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
    storageService.setCurrentProject(project);
  }, []);

  const createProject = useMemo(() => (name: string, description: string) => {
    const newProject = storageService.createNewProject(name, description);
    dispatch({ type: 'ADD_PROJECT', payload: newProject });
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: newProject });
    dispatch({ type: 'TOGGLE_PROJECT_LIST' }); // Hide project list
  }, []);

  const saveProject = useMemo(() => (project: Project) => {
    const updatedProject = storageService.saveProject(project);
    dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject });
  }, []);

  const deleteProject = useMemo(() => (id: string) => {
    storageService.deleteProject(id);
    dispatch({ type: 'DELETE_PROJECT', payload: id });
  }, []);

  const createTemplate = useMemo(() => (name: string, description: string) => {
    const newTemplate = templateService.createNewTemplate(name, description);
    dispatch({ type: 'ADD_TEMPLATE', payload: newTemplate });
    return newTemplate;
  }, []);

  const saveTemplate = useMemo(() => (template: FrameworkTemplate) => {
    templateService.saveTemplate(template);
    dispatch({ type: 'UPDATE_TEMPLATE', payload: template });
  }, []);

  const cloneTemplate = useMemo(() => (id: string, newName: string) => {
    const clonedTemplate = templateService.cloneTemplate(id, newName);
    if (clonedTemplate) {
      dispatch({ type: 'ADD_TEMPLATE', payload: clonedTemplate });
    }
    return clonedTemplate;
  }, []);

  const deleteTemplate = useMemo(() => (id: string) => {
    templateService.deleteTemplate(id);
    dispatch({ type: 'DELETE_TEMPLATE', payload: id });
  }, []);

  const toggleProjectList = useMemo(() => () => {
    dispatch({ type: 'TOGGLE_PROJECT_LIST' });
  }, []);

  const toggleAdminPage = useMemo(() => () => {
    dispatch({ type: 'TOGGLE_ADMIN_PAGE' });
  }, []);

  const toggleDashboardView = useMemo(() => () => {
    dispatch({ type: 'TOGGLE_DASHBOARD_VIEW' });
  }, []);

  const resizeSidebar = useMemo(() => (width: number) => {
    dispatch({ type: 'SET_SIDEBAR_WIDTH', payload: width });
  }, []);

  // Create value object
  const value = useMemo(() => ({
    state,
    dispatch,
    selectProject,
    createProject,
    saveProject,
    deleteProject,
    createTemplate,
    saveTemplate,
    cloneTemplate,
    deleteTemplate,
    toggleProjectList,
    toggleAdminPage,
    toggleDashboardView,
    resizeSidebar,
  }), [
    state, 
    selectProject, 
    createProject, 
    saveProject, 
    deleteProject, 
    createTemplate, 
    saveTemplate,
    cloneTemplate,
    deleteTemplate,
    toggleProjectList,
    toggleAdminPage,
    toggleDashboardView,
    resizeSidebar,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
